import React from 'react';
import { Grid, Card, CardContent, Typography, Box, Chip, CircularProgress } from '@mui/material';
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  TouchApp as TouchAppIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { NotificationMetrics } from '../../../types/notification-admin';

interface MetricsGridProps {
  metrics: NotificationMetrics | null;
  loading?: boolean;
  compact?: boolean;
}

/**
 * MetricsGrid - Grid of key notification metrics cards
 * Displays sent, delivered, read, and actioned notification statistics
 */
const MetricsGrid: React.FC<MetricsGridProps> = ({ metrics, loading = false, compact = false }) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (!metrics) {
    return (
      <Typography variant="body2" color="text.secondary" textAlign="center">
        No metrics available
      </Typography>
    );
  }

  const getPercentageColor = (percentage: number): 'error' | 'warning' | 'success' => {
    if (percentage < 50) return 'error';
    if (percentage < 75) return 'warning';
    return 'success';
  };

  const formatTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${Math.round(minutes)}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const metricCards = [
    {
      title: 'Total Sent',
      value: metrics.totalSent.toLocaleString(),
      icon: <SendIcon sx={{ fontSize: compact ? 28 : 40 }} />,
      color: 'primary.main',
      subtitle: 'Notifications sent',
    },
    {
      title: 'Delivered',
      value: metrics.totalDelivered.toLocaleString(),
      icon: <CheckCircleIcon sx={{ fontSize: compact ? 28 : 40 }} />,
      color: 'success.main',
      subtitle: `${metrics.deliveryRate.toFixed(1)}% delivery rate`,
      percentage: metrics.deliveryRate,
    },
    {
      title: 'Read',
      value: metrics.totalRead.toLocaleString(),
      icon: <VisibilityIcon sx={{ fontSize: compact ? 28 : 40 }} />,
      color: 'info.main',
      subtitle: `${metrics.readRate.toFixed(1)}% read rate`,
      percentage: metrics.readRate,
    },
    {
      title: 'Actioned',
      value: metrics.totalActioned.toLocaleString(),
      icon: <TouchAppIcon sx={{ fontSize: compact ? 28 : 40 }} />,
      color: 'secondary.main',
      subtitle: `${metrics.actionRate.toFixed(1)}% action rate`,
      percentage: metrics.actionRate,
    },
  ];

  return (
    <Grid container spacing={compact ? 2 : 3}>
      {metricCards.map((metric, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box sx={{ color: metric.color }}>{metric.icon}</Box>
                {metric.percentage !== undefined && (
                  <Chip
                    label={`${metric.percentage.toFixed(0)}%`}
                    color={getPercentageColor(metric.percentage)}
                    size="small"
                  />
                )}
              </Box>

              <Typography variant={compact ? 'h5' : 'h4'} fontWeight="bold" gutterBottom>
                {metric.value}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {metric.title}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {metric.subtitle}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}

      {/* Average Time to Read */}
      {!compact && (
        <Grid item xs={12} sm={6} md={3}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box sx={{ color: 'warning.main' }}>
                  <AccessTimeIcon sx={{ fontSize: 40 }} />
                </Box>
              </Box>

              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {formatTime(metrics.avgTimeToRead)}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Avg. Time to Read
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Average response time
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Engagement Score - Optional */}
      {!compact && (
        <Grid item xs={12} sm={6} md={3}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box sx={{ color: metrics.readRate >= 75 ? 'success.main' : 'warning.main' }}>
                  {metrics.readRate >= 75 ? (
                    <TrendingUpIcon sx={{ fontSize: 40 }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: 40 }} />
                  )}
                </Box>
                <Chip
                  label={metrics.readRate >= 75 ? 'Good' : 'Needs Attention'}
                  color={metrics.readRate >= 75 ? 'success' : 'warning'}
                  size="small"
                />
              </Box>

              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {Math.round((metrics.readRate + metrics.actionRate) / 2)}%
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Engagement Score
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Overall user engagement
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default MetricsGrid;
